import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_transforms: number
  active_mappings: number
  records_transformed_today: number
  records_transformed_mtd: number
  transform_accuracy: number
  custom_functions: number
}

interface Transform {
  id: string
  name: string
  source_format: string
  target_format: string
  records_day: number
  status: string
}

interface MappingRule {
  source: string
  target: string
  transform: string
  uses: number
}

export default function DataTransformPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [transforms, setTransforms] = useState<Transform[]>([])
  const [mappingRules, setMappingRules] = useState<MappingRule[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, transformsRes, rulesRes] = await Promise.all([
          fetch('/api/datatransform/statistics'),
          fetch('/api/datatransform/transforms'),
          fetch('/api/datatransform/mappingrules')
        ])
        setStatistics(await statsRes.json())
        setTransforms(await transformsRes.json())
        setMappingRules(await rulesRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Active': 'bg-green-100 text-green-800',
      'Inactive': 'bg-gray-100 text-gray-800',
      'Error': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Data Transform...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Data Transformation</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_transforms}</div>
              <div className="text-sm text-gray-500">Total Transforms</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_mappings}</div>
              <div className="text-sm text-gray-500">Active Mappings</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{(statistics.records_transformed_today / 1000000).toFixed(1)}M</div>
              <div className="text-sm text-gray-500">Records Today</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{(statistics.records_transformed_mtd / 1000000).toFixed(0)}M</div>
              <div className="text-sm text-gray-500">Records MTD</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.transform_accuracy}%</div>
              <div className="text-sm text-gray-500">Accuracy</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.custom_functions}</div>
              <div className="text-sm text-gray-500">Custom Functions</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Data Transformations</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Transform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Records/Day</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {transforms.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{t.name}</td>
                      <td className="px-4 py-3 text-gray-600">{t.source_format}</td>
                      <td className="px-4 py-3 text-gray-600">{t.target_format}</td>
                      <td className="px-4 py-3 text-gray-600">{t.records_day.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(t.status)}`}>
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Mapping Rules</h2></div>
            <div className="p-4 space-y-3">
              {mappingRules.map((rule, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{rule.source} → {rule.target}</span>
                    <span className="text-xs text-gray-500">{rule.uses} uses</span>
                  </div>
                  <div className="text-sm text-blue-600 mt-1">{rule.transform}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
